/**
 * Force-full Stripe phase-2 payment surface.
 *
 * Renders a radio picker box with three kinds of row, all gated by the account's `payment_buttons`
 * preference:
 *   - **Card** — a `<PaymentElement>` expands inline beneath the row, plus a "Complete booking" button.
 *   - **Wallets** (Apple Pay / Google Pay / Link / Amazon Pay / PayPal) — additionally gated by
 *     Stripe's per-browser eligibility probe. Picking one renders a single-method
 *     `<ExpressCheckoutElement>` below the box, showing just that wallet's Stripe-branded button.
 *   - **Klarna** — a wallet row in every respect: probe-gated, and picking it renders the same
 *     single-method `<ExpressCheckoutElement>` with Stripe's own Klarna button and popup. Stripe's
 *     `ExpressCheckoutPaymentMethodsOption` has a `klarna` key, so it needs nothing special.
 *   - **Afterpay-Clearpay / Cash App Pay** — rows of their own alongside the wallets, each rendering a
 *     branded button below the box that behaves like a wallet button: click it and Stripe's own
 *     redirect (Afterpay) or QR modal (Cash App Pay) opens. Stripe's ExpressCheckoutElement has NO
 *     key for either, so there is no Stripe-rendered button to use — instead we draw the button and
 *     call the method's dedicated confirm API (`stripe.confirmAfterpayClearpayPayment` /
 *     `stripe.confirmCashappPayment`), which needs no Elements at all (TRAV23-4560). Gated by
 *     currency, since Stripe validates `payment_method_types` against it at intent creation.
 * Nothing is rendered below the box until the user selects.
 *
 * Architecture note: PaymentElement and the Complete button must share a single `<Elements>` tree
 * to call `stripe.confirmPayment({ elements, ... })` correctly. The wallet-flow uses its own tree
 * because ExpressCheckoutElement is re-keyed on `paymentSelected` to enable only the picked wallet
 * — separating the trees keeps that re-key from disturbing PaymentElement's mount. We therefore
 * mount one of two `<Elements>` providers depending on `paymentSelected` (a PaymentElement method →
 * PaymentElement tree; a wallet or nothing → wallet tree). Both use `mode: 'payment'`. Switching
 * selection re-mounts the active tree; the picker is controlled by parent state so the user's
 * selection survives the re-mount. `multiplePayments` (the wallet probe result) is also lifted into
 * this component's state so it survives swaps between the two trees.
 *
 * So there are three confirm paths, one per row kind: Elements+PaymentElement (Card only, still pinned
 * to `paymentMethodTypes: ['card']` exactly as before), ExpressCheckoutElement (wallets + Klarna, on
 * automatic payment methods as always), and the direct confirm APIs (Afterpay, Cash App Pay). The
 * intent must declare whatever the client confirms, so the booking request carries
 * `stripePaymentMethodType` (with `cardOnly` unchanged for the card path); the wallet path sends
 * neither and stays on automatic methods.
 *
 * Note on the ExpressCheckoutElement's `paymentMethods` option: every key Stripe supports has to be
 * listed there. An omitted key defaults to `'auto'`, so it will render alongside the one the customer
 * picked — which is what made a stray Klarna button appear next to Amazon Pay before `klarna` was
 * added to the map.
 *
 * The wallet `<ExpressCheckoutElement>` doubles as the wallet-eligibility probe: when nothing is
 * selected yet we mount it with all wallets enabled, hide it via `display: none`, and rely on
 * `onReady` to populate `multiplePayments` so the picker's wallet rows can render. As soon as the
 * user picks a wallet, the EC's `paymentMethods` options are re-keyed to enable only that wallet
 * and the wrapper becomes visible.
 *
 * Engines pass their identity via `type` (StripeBtnTypes). Per-engine payload construction in the
 * card-flow `runSubmit` and wallet-flow `onConfirm` mirrors the patterns already in
 * `../component.tsx` (ExpressCheckoutButtons) and `../stripe-checkout-buttons/component.tsx`
 * (StripeCheckoutButtons); keep them in sync.
 */

import './style.scss';

import { useEffect, useMemo, useState } from 'react';
import { Card, Radio, Divider, Spin } from 'antd';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { AmexCardSvg, MaestroCardSvg, MasterCardSvg, VisaCardSvg } from '@assets';
import {
  AfterpayClearpaySvg,
  AmazonPaySvg,
  ApplePaySvg,
  CashAppPaySvg,
  GooglePaySvg,
  KlarnaPaySvg,
  LinkPaySvg,
  PaypalSvg,
} from '../../../../assets/icons/payment-select';
import { BlueButton } from '@share/components';
import { WhiteButton } from '../../../../components/components/common/white-button';
import { ISessionKey } from '@share/common-types';
import {
  applyExpressWalletSsoToPayload,
  buildBillingAddressPayload,
  buildCarsBillingAddressPayload,
  buildGuestBillingAddressPayload,
  computeIsStripePaymentMethodsEnabled,
  FunctionalRootState,
  getAccountPaymentButtonKeys,
  getExpressCheckoutExtraMethods,
  isStripeDirectConfirmMethod,
  getPaymentElementExtraMethods,
  getSessionObj,
  hasStripeExpressWalletMethods,
  isEnableCreditLine,
  isPaymentButtonsPrefEmpty,
  UrlUtils,
} from '@share/utils';
import { CONDO_SESSION_KEY, SESSION_KEY_LABEL, SESSION_KEY_LABEL_ROOMS } from '@constants';
import {
  getBookPackageWithCard,
  getCarsBookPackageWithCard,
  getCondoBookPackageWithCard,
  getWeeksBookPackageWithCard,
  ICarsDetailsState,
  ICarsReviewBookState,
  ICondoReviewBookState,
  ICouponState,
  IReviewBookState,
  IWeeksReviewBookState,
} from '@store/slices';
import { IHotelsState, ILoginState, IMarginatorState } from '@share/store/slices';
import {
  CONDO_UNITS_SESSION_KEY_LABEL,
  DATE_FORMAT_FILTER,
  GET_CUSTOMER_LOYALTY,
  QUOTE_LABEL,
  WEEKS_UNITS_SESSION_KEY_LABEL,
} from '@share/constants';
import type { StripeDirectConfirm, StripeDirectConfirmBillingDetails } from '@share/utils';
import { StripeBtnTypes, stripePromise } from '../../../utils/stripe';
import {
  getAvailabilityId,
  getCondoId,
  getWeeksAvailabilityId,
  removingDashFromString,
  getCountryIsoCode,
} from '@utils';

/** Fallback until the PaymentElement reports a selection — it opens on card. */
const PAYMENT_ELEMENT_DEFAULT_METHOD = 'card';

/** BFF `PaymentDescripcion` for a payment taken through the PaymentElement. The element reports the
 *  method the customer actually chose via `onChange.value.type` (Stripe's `payment_method_types`
 *  spelling), so a Klarna or Cash App Pay booking is no longer labelled "Card" in backoffice. */
function paymentDescriptionFromElementType(elementPaymentType: string | undefined): string {
  const key = (elementPaymentType || PAYMENT_ELEMENT_DEFAULT_METHOD).toLowerCase();
  const map: Record<string, string> = {
    card: 'Card',
    afterpay_clearpay: 'Afterpay Clearpay',
    cashapp: 'Cash App Pay',
  };
  return map[key] ?? key.replace(/_/g, ' ');
}

/** Force-full PaymentElement: accordion expanded, in-element wallet buttons suppressed (the
 *  ExpressCheckoutElement owns Apple/Google Pay). The `<Elements>` tree pins `paymentMethodTypes` to
 *  the single method the selected picker row stands for, so the element only ever renders that one
 *  method's form and this stays a plain display option. */
const FORCE_FULL_PAYMENT_ELEMENT_OPTIONS = {
  layout: {
    type: 'accordion' as const,
    defaultCollapsed: false,
  },
  wallets: {
    applePay: 'never' as const,
    googlePay: 'never' as const,
  },
};

/** Picker-row artwork for the methods added in TRAV23-4560. Stripe ships button artwork for the
 *  original wallets but not for these, so each row carries its own brand mark. */
const EXTRA_METHOD_ICONS: Record<string, React.FC> = {
  klarna: KlarnaPaySvg,
  afterpay_clearpay: AfterpayClearpaySvg,
  cashapp: CashAppPaySvg,
};

/** Picker-row labels for those methods — brand names, so not translated. */
const EXTRA_METHOD_LABELS: Record<string, string> = {
  klarna: 'Klarna',
  afterpay_clearpay: 'Afterpay / Clearpay',
  cashapp: 'Cash App Pay',
};

interface IProps {
  type: StripeBtnTypes;
  amount: number;
  currency: string | undefined;
  totalPrice: number;
  isDisabled?: boolean;
  scrollToError?: () => void;
  /** "Back" button — when provided, rendered alongside the active action so the user can return to
   *  the details phase. */
  forceFullStripeBack?: {
    onClick: () => void;
    disabled?: boolean;
  };
}

/** BFF `PaymentDescripcion` query param — derived from the ExpressCheckoutElement `confirm` event. */
function paymentDescriptionFromExpressType(expressPaymentType: string | undefined): string {
  if (!expressPaymentType) return 'Express checkout';
  const key = String(expressPaymentType).toLowerCase().replace(/-/g, '_');
  const map: Record<string, string> = {
    google_pay: 'Google pay',
    apple_pay: 'Apple pay',
    amazon_pay: 'Amazon pay',
    paypal: 'Paypal',
    link: 'Link',
    klarna: 'Klarna',
  };
  return map[key] ?? String(expressPaymentType).replace(/_/g, ' ');
}

export default function ForceFullStripeCheckout(props: IProps) {
  const { type, amount, currency, totalPrice, isDisabled, scrollToError, forceFullStripeBack } = props;

  const { account }: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);

  const [paymentSelected, setPaymentSelected] = useState<string>('');
  const [multiplePayments, setMultiplePayments] = useState<any>({});
  /** True until Stripe's ExpressCheckoutElement reports `availablePaymentMethods` for the first
   *  time. While true the picker shows a spinner instead of partial rows. Switched off once on the
   *  first onReady (in either probe or single-wallet mode) and stays off for the rest of the
   *  session — switching between Card and a wallet must not flip it back on. */
  const [isProbing, setIsProbing] = useState<boolean>(true);

  /** The probe resolves on the ExpressCheckoutElement's first `onReady`, which never fires when the
   *  browser has no eligible wallet at all — without a cap the picker would spin forever. Mirrors the
   *  8s fallback in `../component.tsx` (ExpressCheckoutButtons). Newly relevant now that an account
   *  can enable only Klarna / Afterpay / Cash App Pay and no wallet. */
  useEffect(() => {
    const timer = window.setTimeout(() => setIsProbing(false), 8000);
    return () => window.clearTimeout(timer);
  }, []);

  const paymentButtonKeys = useMemo(() => getAccountPaymentButtonKeys(account), [account]);
  /** Afterpay / Cash App Pay this account has enabled and that Stripe accepts for this currency.
   *  Each becomes a picker row with a branded button, confirmed through its own Stripe API — the
   *  ExpressCheckoutElement has no key for either. */
  const directConfirmMethods = useMemo(
    () => getPaymentElementExtraMethods(paymentButtonKeys, currency),
    [paymentButtonKeys, currency],
  );
  /** Klarna — a wallet row like Google/Apple/Link, still subject to Stripe's eligibility probe. */
  const expressCheckoutExtraMethods = useMemo(
    () => getExpressCheckoutExtraMethods(paymentButtonKeys),
    [paymentButtonKeys],
  );

  /** Card is the only row still backed by a `<PaymentElement>`. */
  const isCardRow = paymentSelected === PAYMENT_ELEMENT_DEFAULT_METHOD;
  /** Afterpay / Cash App Pay: branded button + dedicated confirm API, no Elements involved. */
  const isDirectConfirmRow =
    isStripeDirectConfirmMethod(paymentSelected) && directConfirmMethods.includes(paymentSelected);

  const paymentElementOptions = useMemo(
    () => ({
      mode: 'payment' as const,
      // Must match the BFF PaymentIntent (manual capture); capture happens after the provider booking.
      captureMethod: 'manual' as const,
      amount,
      currency: (currency ?? 'usd').toLowerCase(),
      // Card only, exactly as before — the other rows never mount this tree.
      paymentMethodTypes: [PAYMENT_ELEMENT_DEFAULT_METHOD],
    }),
    [amount, currency],
  );

  /** The wallet tree leaves method selection to the ExpressCheckoutElement, so no `paymentMethodTypes`. */
  const walletOptions = useMemo(
    () => ({
      mode: 'payment' as const,
      captureMethod: 'manual' as const,
      amount,
      currency: (currency ?? 'usd').toLowerCase(),
    }),
    [amount, currency],
  );

  /** When the account has no `payment_buttons` preference configured, skip the picker entirely and
   *  render only the card form + Complete button. Stripe is still in use (PaymentElement); the user
   *  just has no other methods to choose from, so there's nothing to pick between. */
  if (isPaymentButtonsPrefEmpty(account)) {
    return (
      <Elements stripe={stripePromise} options={paymentElementOptions} key="ffsc-card-only">
        <Card className="force-full-stripe-checkout__picker mb-3">
          <PaymentElement options={FORCE_FULL_PAYMENT_ELEMENT_OPTIONS} />
        </Card>
        <CardCompleteAction
          type={type}
          totalPrice={totalPrice}
          isDisabled={isDisabled}
          paymentElementMethod={PAYMENT_ELEMENT_DEFAULT_METHOD}
          forceFullStripeBack={forceFullStripeBack}
        />
      </Elements>
    );
  }

  if (isCardRow) {
    return (
      <Elements stripe={stripePromise} options={paymentElementOptions} key="ffsc-card">
        <PickerBox
          paymentSelected={paymentSelected}
          onSelectMethod={setPaymentSelected}
          multiplePayments={multiplePayments}
          isProbing={isProbing}
          directConfirmMethods={directConfirmMethods}
          expressCheckoutExtraMethods={expressCheckoutExtraMethods}
          inlineElement={
            <div className="force-full-stripe-checkout__card-inline">
              <PaymentElement options={FORCE_FULL_PAYMENT_ELEMENT_OPTIONS} />
            </div>
          }
        />
        <CardCompleteAction
          type={type}
          totalPrice={totalPrice}
          isDisabled={isDisabled}
          paymentElementMethod={PAYMENT_ELEMENT_DEFAULT_METHOD}
          forceFullStripeBack={forceFullStripeBack}
        />
      </Elements>
    );
  }

  if (isDirectConfirmRow) {
    // No Elements tree of its own: the dedicated confirm APIs take a clientSecret, not an Element.
    // We still mount one so `useStripe()` resolves inside `CardCompleteAction`.
    return (
      <Elements stripe={stripePromise} options={walletOptions} key="ffsc-direct">
        <PickerBox
          paymentSelected={paymentSelected}
          onSelectMethod={setPaymentSelected}
          multiplePayments={multiplePayments}
          isProbing={isProbing}
          directConfirmMethods={directConfirmMethods}
          expressCheckoutExtraMethods={expressCheckoutExtraMethods}
          inlineElement={null}
        />
        <CardCompleteAction
          type={type}
          totalPrice={totalPrice}
          isDisabled={isDisabled}
          paymentElementMethod={paymentSelected}
          forceFullStripeBack={forceFullStripeBack}
        />
      </Elements>
    );
  }

  return (
    <Elements stripe={stripePromise} options={walletOptions} key="ffsc-wallet">
      <PickerBox
        paymentSelected={paymentSelected}
        onSelectMethod={setPaymentSelected}
        multiplePayments={multiplePayments}
        isProbing={isProbing}
        directConfirmMethods={directConfirmMethods}
        expressCheckoutExtraMethods={expressCheckoutExtraMethods}
        inlineElement={null}
      />
      <WalletECAction
        paymentSelected={paymentSelected}
        onMultiplePaymentsResolved={setMultiplePayments}
        onProbeComplete={() => setIsProbing(false)}
        type={type}
        scrollToError={scrollToError}
        forceFullStripeBack={forceFullStripeBack}
        isDisabled={isDisabled}
      />
    </Elements>
  );
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
 * Picker box (radio rows)
 * ────────────────────────────────────────────────────────────────────────────────────────────── */

interface PickerBoxProps {
  paymentSelected: string;
  onSelectMethod: (value: string) => void;
  multiplePayments: any;
  /** While true the WALLET rows render a spinner instead of themselves — Stripe is still probing
   *  which wallet methods are eligible for this browser/device. Card and the PaymentElement-only
   *  methods do not depend on that probe, so they render immediately. */
  isProbing: boolean;
  /** Afterpay / Cash App Pay rows to render (already filtered by preference + currency). Backed by a
   *  branded button + the method's dedicated confirm API. */
  directConfirmMethods: string[];
  /** Klarna row(s) to render (already filtered by preference). Backed by the ExpressCheckoutElement,
   *  so additionally gated below by the eligibility probe, exactly like the wallets. */
  expressCheckoutExtraMethods: string[];
  /** Rendered immediately under the selected PaymentElement row (Card or one of the extras). */
  inlineElement: React.ReactNode;
}

function PickerBox({
  paymentSelected,
  onSelectMethod,
  multiplePayments,
  isProbing,
  directConfirmMethods,
  expressCheckoutExtraMethods,
  inlineElement,
}: PickerBoxProps) {
  const intl = useIntl();
  const { account }: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);

  const paymentButtonKeys = useMemo(() => getAccountPaymentButtonKeys(account), [account]);
  const noWalletPrefs = isEmpty(paymentButtonKeys);

  /** Card first, then the wallets (Klarna among them), then the PaymentElement-only methods — so all
   *  three new methods sit alongside Google Pay and friends as peer choices.
   *
   *  The whole set is withheld behind a spinner until the wallet probe resolves: the probe lives in
   *  `WalletECAction`, which is only mounted on the wallet branch, so letting the customer pick a
   *  PaymentElement row first would unmount it and the wallet rows would never appear. The parent
   *  caps the wait (see its probe fallback), so the spinner cannot outlive the probe. */
  const options = [
    {
      value: 'card',
      label: <FormattedMessage id="debit.credit.card" />,
      Icon: () => (
        <div className="force-full-stripe-checkout__card-icons">
          <MaestroCardSvg />
          <MasterCardSvg />
          <VisaCardSvg />
          <AmexCardSvg />
        </div>
      ),
    },
    !noWalletPrefs &&
      multiplePayments?.googlePay &&
      paymentButtonKeys.includes('google_pay') && {
        value: 'google_pay',
        label: 'Google Pay',
        Icon: GooglePaySvg,
      },
    !noWalletPrefs &&
      multiplePayments?.applePay &&
      paymentButtonKeys.includes('apple_pay') && {
        value: 'apple_pay',
        label: 'Apple Pay',
        Icon: ApplePaySvg,
      },
    !noWalletPrefs &&
      multiplePayments?.link &&
      paymentButtonKeys.includes('link') && {
        value: 'link',
        label: 'Link',
        Icon: LinkPaySvg,
      },
    !noWalletPrefs &&
      multiplePayments?.paypal &&
      paymentButtonKeys.includes('paypal') && {
        value: 'paypal',
        label: 'PayPal',
        Icon: PaypalSvg,
      },
    !noWalletPrefs &&
      multiplePayments?.amazonPay &&
      paymentButtonKeys.includes('amazon_pay') && {
        value: 'amazon_pay',
        label: 'Amazon Pay',
        Icon: AmazonPaySvg,
      },
    // Klarna & co: wallet rows, so gated by the same eligibility probe as Google/Apple/Link.
    ...expressCheckoutExtraMethods.map((method) =>
      !noWalletPrefs && multiplePayments?.[method]
        ? {
            value: method,
            label: EXTRA_METHOD_LABELS[method] ?? method,
            Icon: EXTRA_METHOD_ICONS[method],
          }
        : false,
    ),
    // Afterpay / Cash App Pay: no express button exists, so no probe to gate on.
    ...directConfirmMethods.map((method) => ({
      value: method,
      label: EXTRA_METHOD_LABELS[method] ?? method,
      Icon: EXTRA_METHOD_ICONS[method],
    })),
  ].filter(Boolean) as { value: string; label: React.ReactNode; Icon: React.FC }[];

  return (
    <Card className="force-full-stripe-checkout__picker mb-3">
      <p className="force-full-stripe-checkout__title">
        {intl.formatMessage({ id: 'select.payment.to.pay' })}
      </p>
      {isProbing ? (
        <div className="force-full-stripe-checkout__probe-loader">
          <Spin />
        </div>
      ) : (
      <Radio.Group
        onChange={(e) => onSelectMethod(e.target.value)}
        value={paymentSelected}
        className="force-full-stripe-checkout__radio-group"
      >
        {options.map((opt, index) => {
          const isLast = index === options.length - 1;
          const showInline = paymentSelected === opt.value && inlineElement;
          return (
            <div key={opt.value} className="force-full-stripe-checkout__option-wrapper">
              <div className="force-full-stripe-checkout__option">
                <Radio value={opt.value} className="force-full-stripe-checkout__radio">
                  <span className="force-full-stripe-checkout__option-text">{opt.label}</span>
                </Radio>
                <opt.Icon />
              </div>
              {showInline && (
                <div className="force-full-stripe-checkout__inline-slot">{inlineElement}</div>
              )}
              {!isLast && <Divider className="force-full-stripe-checkout__divider" />}
            </div>
          );
        })}
      </Radio.Group>
      )}
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
 * Card path — Complete button (calls stripe.confirmPayment via the booking thunks)
 * ────────────────────────────────────────────────────────────────────────────────────────────── */

interface CardCompleteActionProps {
  type: StripeBtnTypes;
  totalPrice: number;
  isDisabled?: boolean;
  /** Method selected inside the PaymentElement (`card`, `klarna`, `cashapp`, …). */
  paymentElementMethod: string;
  forceFullStripeBack?: { onClick: () => void; disabled?: boolean };
}

function CardCompleteAction({ type, totalPrice, isDisabled, paymentElementMethod, forceFullStripeBack }: CardCompleteActionProps) {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const reviewBookStore: IReviewBookState = useSelector(
    (state: FunctionalRootState) => state.reviewBookStore,
  );
  const marginatorStore: IMarginatorState = useSelector(
    (state: FunctionalRootState) => state.marginatorStore,
  );
  const carsReviewBookStore: ICarsReviewBookState = useSelector(
    (state: FunctionalRootState) => state.carsReviewBookStore,
  );
  const carsDetailsStore: ICarsDetailsState = useSelector(
    (state: FunctionalRootState) => state.carsDetailsStore,
  );
  const weeksReviewBookStore: IWeeksReviewBookState = useSelector(
    (state: FunctionalRootState) => state.weeksReviewBookStore,
  );
  const { account, userWallet }: ILoginState = useSelector(
    (state: FunctionalRootState) => state.loginStore,
  );
  const {
    condoBookingSummary,
    guest: condoGuest,
    loadingBooking: condoLoadingBooking,
    stripeLoading: condoStripeLoading,
    selectedCondoReviewClientCash,
  }: ICondoReviewBookState = useSelector(
    (state: FunctionalRootState) => state.condoReviewBookStore,
  );
  const { condosCountriesCode } = useSelector(
    (state: FunctionalRootState) => state.condosStaticStore,
  );
  const { coupon }: ICouponState = useSelector(
    (state: FunctionalRootState) => state.redeemCodeStore,
  );
  const { agencyNumber, referenceNumber }: IHotelsState = useSelector(
    (state: FunctionalRootState) => state.hotelsStore,
  );

  const {
    guests,
    bookingSummary,
    loadingBooking,
    stripeLoading,
    selectedHotelReviewClientCash,
    card,
  } = reviewBookStore;
  const {
    stripe: stripeInfo,
    driver,
    loadingBooking: carsLoadingBooking,
    stripeLoading: carsStripeLoading,
    selectedCarsReviewClientCash,
  } = carsReviewBookStore;
  const { car } = carsDetailsStore;
  const {
    weeksBookingSummary,
    guest: weeksGuest,
    loadingBooking: weeksLoadingBooking,
    selectedWeeksReviewClientCash,
  } = weeksReviewBookStore;

  const loading =
    type === StripeBtnTypes.Hotel
      ? loadingBooking || stripeLoading
      : type === StripeBtnTypes.Condo
        ? condoLoadingBooking || condoStripeLoading
        : type === StripeBtnTypes.Cars
          ? carsLoadingBooking || carsStripeLoading
          : weeksLoadingBooking;

  /** Afterpay / Cash App Pay: no Elements to collect or submit, just a branded button that hands the
   *  clientSecret to the method's own confirm API. */
  const isDirectConfirm = isStripeDirectConfirmMethod(paymentElementMethod);

  const buttonDisabled = Boolean(
    isDisabled || !stripe || (!isDirectConfirm && !elements) || loading,
  );

  /** The picker row the customer chose — persisted as the payment description so a Cash App Pay
   *  booking is not labelled "Card" in backoffice. */
  const paymentDescription = paymentDescriptionFromElementType(paymentElementMethod);
  /** The BFF PaymentIntent has to declare whatever the client is about to confirm. `cardOnly` is the
   *  existing boolean it already understands (→ `payment_method_types:['card']`) and stays exactly as
   *  it was for the card path; `stripePaymentMethodType` carries the method for the direct rows. */
  const cardOnly = paymentElementMethod === PAYMENT_ELEMENT_DEFAULT_METHOD;
  const stripePaymentMethodType = paymentElementMethod;

  /** Billing details Stripe needs to build the PaymentMethod on the direct-confirm path. Afterpay
   *  requires name, email and address; Cash App Pay requires none, but sending them is harmless.
   *  Sourced per engine from the same store data the booking payload below is built from. */
  const buildDirectConfirm = (): StripeDirectConfirm | undefined => {
    if (!isDirectConfirm) return undefined;
    let billingDetails: StripeDirectConfirmBillingDetails | undefined;
    if (type === StripeBtnTypes.Cars) {
      billingDetails = {
        name: [driver?.givenName, driver?.surname].filter(Boolean).join(' ') || undefined,
        email: driver?.email,
        phone: driver?.phone,
        address: {
          line1: stripeInfo?.addressLine,
          city: stripeInfo?.city,
          state: stripeInfo?.state,
          postal_code: stripeInfo?.zipCode,
          country: stripeInfo?.country ?? driver?.country,
        },
      };
    } else if (type === StripeBtnTypes.Hotel) {
      const leadPax: any = guests.find((guest: any) => guest?.isMainGuest) ?? guests[0];
      billingDetails = {
        name: [leadPax?.givenName, leadPax?.surname].filter(Boolean).join(' ') || undefined,
        email: leadPax?.email,
        phone: leadPax?.phone,
      };
    } else {
      const guest: any = type === StripeBtnTypes.Condo ? condoGuest : weeksGuest;
      billingDetails = {
        name: [guest?.givenName, guest?.surname].filter(Boolean).join(' ') || undefined,
        email: guest?.email,
        phone: guest?.phone,
      };
    }
    return { paymentMethodType: paymentElementMethod, billingDetails };
  };
  const directConfirm = buildDirectConfirm();

  const runSubmit = async () => {
    if (!stripe) return;

    // Only the card path collects through Elements, so only it has anything to submit.
    if (!isDirectConfirm) {
      if (!elements) return;
      const { error: submitError } = await elements.submit();
      if (submitError) return;
    }

    const values = UrlUtils.getValues();
    const quote = values[QUOTE_LABEL] ? (values[QUOTE_LABEL] as string) : undefined;

    if (type === StripeBtnTypes.Cars) {
      const carsData: any = {
        id: car?.id,
        customer: {
          primary: {
            personName: { givenName: driver?.givenName, surname: driver?.surname },
            telephone: { phoneTechType: '1', phoneNumber: driver?.phone },
            email: driver?.email,
            address: {
              streetNmbr: stripeInfo?.addressLine,
              cityName: stripeInfo?.city,
              postalCode: stripeInfo?.zipCode,
              stateCode: stripeInfo?.state ? stripeInfo?.state : 'AK',
              stateProv: stripeInfo?.state ? stripeInfo?.state : 'AK',
              countryCode: driver?.country,
            },
            countryCode: stripeInfo?.country,
            birthDate: `${moment(driver?.birthDate, DATE_FORMAT_FILTER).format('yyyy-MM-DD')}`,
            language: 'EN',
          },
        },
        currency: account?.currency,
        marginator: {
          percentage: marginatorStore.percentage,
          commission: marginatorStore.commission,
          impersonator: { memberId: marginatorStore.impersonator.memberId },
        },
        // PaymentElement flow → the intent must declare the same single method the element is pinned
        // to. See `cardOnly` / `stripePaymentMethodType` above.
        cardOnly,
        stripePaymentMethodType,
        isCheckoutExpress: true,
      };
      applyExpressWalletSsoToPayload(
        carsData,
        { account, userWallet, selectedClientCash: selectedCarsReviewClientCash },
        { carsImpersonationLifeType: true },
      );
      // No `card` goes out on express, so the billing address the guest typed rides along as
      // `billingAddress` — `customer.primary.address` only reaches the booking record, not the
      // payment record. Initial request only: the express resume is a GET and cannot take it.
      const billingAddress = buildCarsBillingAddressPayload(stripeInfo, driver);
      if (billingAddress) {
        carsData.billingAddress = billingAddress;
      }
      dispatch(
        getCarsBookPackageWithCard(
          carsData,
          stripe as any,
          elements as any,
          paymentDescription,
          directConfirm,
        ),
      );
      return;
    }

    if (type === StripeBtnTypes.Hotel) {
      const sessionKey = getSessionObj(
        values[SESSION_KEY_LABEL_ROOMS] as ISessionKey,
        values[SESSION_KEY_LABEL] as ISessionKey,
      );
      const getLeadPax = guests.find((guest: any) => guest?.isMainGuest);
      const data: any = {
        sessionKey,
        bookingPrice: totalPrice,
        // PaymentElement flow → the intent must declare the same single method the element is pinned
        // to. See `cardOnly` / `stripePaymentMethodType` above.
        cardOnly,
        stripePaymentMethodType,
        hotelId: bookingSummary?.bookingCard.package.hotelId,
        leadPaxId: getLeadPax?.id,
        leadPaxRoomId: getLeadPax?.allocation,
        packageId: bookingSummary?.bookingCard.package.packageId,
        marginator: {
          percentage: marginatorStore.percentage,
          commission: marginatorStore.commission,
          impersonator: { memberId: marginatorStore.impersonator.memberId },
        },
        isCheckoutExpress: true,
        guests: guests.map((guest: any) => ({
          ...guest,
          namePrefix: !isEmpty(guest?.namePrefix) ? guest?.namePrefix : 'Mr',
          givenName: !isEmpty(guest?.givenName) ? guest?.givenName : 'TBV',
          surname: !isEmpty(guest?.surname) ? guest?.surname : 'TBV',
          type: guest?.type,
        })),
        currency: account?.currency,
        referenceNumber,
        agencyNumber,
        couponCode: coupon,
        quote,
      };
      applyExpressWalletSsoToPayload(data, {
        account,
        userWallet,
        selectedClientCash: selectedHotelReviewClientCash,
      });
      // Force-full sends no `card` (the PaymentElement holds it), so the address collected by the
      // address-only form on the details page rides along as `billingAddress`.
      const billingAddress = buildBillingAddressPayload({
        ...card,
        holderName: [getLeadPax?.givenName, getLeadPax?.surname].filter(Boolean).join(' '),
        phone: card?.phone || getLeadPax?.phone,
      });
      if (billingAddress) {
        data.billingAddress = billingAddress;
      }
      const customerLoyalty = localStorage.getItem(GET_CUSTOMER_LOYALTY) ?? '';
      if (!isEmpty(customerLoyalty)) {
        data.customerLoyalty = customerLoyalty;
      }
      dispatch(
        getBookPackageWithCard(data, stripe as any, elements as any, paymentDescription, directConfirm),
      );
      return;
    }

    if (type === StripeBtnTypes.Condo) {
      const sessionKey = getSessionObj(
        values[CONDO_UNITS_SESSION_KEY_LABEL] as ISessionKey,
        values[CONDO_SESSION_KEY] as ISessionKey,
      );
      const data: any = {
        sessionKey,
        bookingPrice: totalPrice,
        condoId: getCondoId(),
        availabilityId: getAvailabilityId(),
        couponCode: coupon,
        guests: condoGuest
          ? [{ ...condoGuest, phone: removingDashFromString(condoGuest.phone || '') }]
          : [],
        supplierType: condoBookingSummary?.supplierType ?? '',
        quote,
        // PaymentElement flow → the intent must declare the same single method the element is pinned
        // to. See `cardOnly` / `stripePaymentMethodType` above.
        cardOnly,
        stripePaymentMethodType,
        isCheckoutExpress: true,
      };
      applyExpressWalletSsoToPayload(data, {
        account,
        userWallet,
        selectedClientCash: selectedCondoReviewClientCash,
      });
      // No `card` goes out on express, so the primary address rides along as `billingAddress` —
      // inside `guests[]` it only reaches the booking record, not the payment record. Initial
      // request only: the express resume is a GET and cannot take it.
      const billingAddress = buildGuestBillingAddressPayload(
        condoGuest,
        getCountryIsoCode(condoGuest?.country, condoBookingSummary?.supplierType, condosCountriesCode),
      );
      if (billingAddress) {
        data.billingAddress = billingAddress;
      }
      dispatch(
        getCondoBookPackageWithCard(
          data,
          () => {},
          stripe as any,
          elements as any,
          paymentDescription,
          directConfirm,
        ),
      );
      return;
    }

    if (type === StripeBtnTypes.Weeks) {
      const sessionKey = getSessionObj(
        values[WEEKS_UNITS_SESSION_KEY_LABEL] as ISessionKey,
        values[CONDO_SESSION_KEY] as ISessionKey,
      );
      const data: any = {
        sessionKey,
        bookingPrice: totalPrice,
        condoId: getCondoId() ? getCondoId() : null,
        availabilityId: getWeeksAvailabilityId() ? getWeeksAvailabilityId() : null,
        couponCode: coupon,
        guests: weeksGuest
          ? [{ ...weeksGuest, phone: removingDashFromString(weeksGuest.phone || '') }]
          : [],
        supplierType: weeksBookingSummary?.supplierType ?? '',
        quote,
        // PaymentElement flow → the intent must declare the same single method the element is pinned
        // to. See `cardOnly` / `stripePaymentMethodType` above.
        cardOnly,
        stripePaymentMethodType,
        isCheckoutExpress: true,
      };
      applyExpressWalletSsoToPayload(data, {
        account,
        userWallet,
        selectedClientCash: selectedWeeksReviewClientCash,
      });
      // No `card` goes out on express, so the primary address rides along as `billingAddress` —
      // inside `guests[]` it only reaches the booking record, not the payment record. Initial
      // request only: the express resume is a GET and cannot take it.
      const billingAddress = buildGuestBillingAddressPayload(
        weeksGuest,
        getCountryIsoCode(weeksGuest?.country, weeksBookingSummary?.supplierType, condosCountriesCode),
      );
      if (billingAddress) {
        data.billingAddress = billingAddress;
      }
      dispatch(
        (getWeeksBookPackageWithCard as any)(
          data,
          () => {},
          stripe as any,
          elements as any,
          paymentDescription,
          directConfirm,
        ),
      );
    }
  };

  return (
    <div className="force-full-stripe-checkout__action force-full-stripe-checkout__action--card">
      {forceFullStripeBack ? (
        <WhiteButton
          htmlType="button"
          className="force-full-stripe-checkout__back"
          onClick={forceFullStripeBack.onClick}
          disabled={forceFullStripeBack.disabled}
        >
          <FormattedMessage id="review.book.checkout.back" />
        </WhiteButton>
      ) : null}
      {isDirectConfirm ? (
        // Stands in for the Stripe-rendered wallet button: same slot, same one-click behaviour, and
        // clicking it opens Afterpay's redirect or Cash App Pay's QR modal.
        <button
          type="button"
          className={`force-full-stripe-checkout__direct-button force-full-stripe-checkout__direct-button--${paymentElementMethod}`}
          onClick={runSubmit}
          disabled={buttonDisabled}
        >
          {EXTRA_METHOD_LABELS[paymentElementMethod] ?? paymentElementMethod}
          {loading ? <Spin /> : null}
        </button>
      ) : (
        <BlueButton
          className="force-full-stripe-checkout__complete"
          onClick={runSubmit}
          disabled={buttonDisabled}
        >
          <FormattedMessage id="complete.booking" />
          {loading ? <Spin /> : null}
        </BlueButton>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
 * Wallet path — single-method ExpressCheckoutElement (also doubles as the wallet eligibility probe
 * when paymentSelected === '')
 * ────────────────────────────────────────────────────────────────────────────────────────────── */

interface WalletECActionProps {
  paymentSelected: string;
  onMultiplePaymentsResolved: (available: any) => void;
  /** Called once after Stripe's first `onReady` so the parent can flip its `isProbing` flag off. */
  onProbeComplete: () => void;
  type: StripeBtnTypes;
  scrollToError?: () => void;
  forceFullStripeBack?: { onClick: () => void; disabled?: boolean };
  isDisabled?: boolean;
}

function WalletECAction(props: WalletECActionProps) {
  const { paymentSelected, onMultiplePaymentsResolved, onProbeComplete, type, scrollToError, forceFullStripeBack } = props;

  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const reviewBookStore: IReviewBookState = useSelector(
    (state: FunctionalRootState) => state.reviewBookStore,
  );
  const marginatorStore: IMarginatorState = useSelector(
    (state: FunctionalRootState) => state.marginatorStore,
  );
  const carsReviewBookStore: ICarsReviewBookState = useSelector(
    (state: FunctionalRootState) => state.carsReviewBookStore,
  );
  const carsDetailsStore: ICarsDetailsState = useSelector(
    (state: FunctionalRootState) => state.carsDetailsStore,
  );
  const weeksReviewBookStore: IWeeksReviewBookState = useSelector(
    (state: FunctionalRootState) => state.weeksReviewBookStore,
  );
  const { account, userWallet }: ILoginState = useSelector(
    (state: FunctionalRootState) => state.loginStore,
  );
  const navigationMenuStore = useSelector(
    (state: FunctionalRootState) => state.navigationMenuStore,
  );
  const {
    condoBookingSummary,
    guest: condoGuest,
    selectedCondoReviewClientCash,
  }: ICondoReviewBookState = useSelector(
    (state: FunctionalRootState) => state.condoReviewBookStore,
  );
  const { condosCountriesCode } = useSelector(
    (state: FunctionalRootState) => state.condosStaticStore,
  );
  const { coupon }: ICouponState = useSelector(
    (state: FunctionalRootState) => state.redeemCodeStore,
  );
  const { agencyNumber, referenceNumber }: IHotelsState = useSelector(
    (state: FunctionalRootState) => state.hotelsStore,
  );

  const { guests, bookingSummary, selectedHotelReviewClientCash, card } = reviewBookStore;
  const { stripe: stripeInfo, driver, selectedCarsReviewClientCash } = carsReviewBookStore;
  const { car } = carsDetailsStore;
  const { weeksBookingSummary, guest: weeksGuest, selectedWeeksReviewClientCash } =
    weeksReviewBookStore;

  const isStripeExpressEnabled = computeIsStripePaymentMethodsEnabled(
    account,
    navigationMenuStore?.items?.siteId,
    navigationMenuStore?.items?.organizationId,
  );

  /** Probe-mode (`paymentSelected === ''`): allow every method so onReady can report eligibility.
   *  Selected mode: enable only the chosen one, force the rest to `'never'`.
   *
   *  EVERY key Stripe supports has to be listed, including the ones we don't want. An omitted key
   *  defaults to `'auto'`, so leaving `klarna` out made Stripe render a Klarna button next to the
   *  selected wallet whenever the Stripe account had Klarna on. */
  const paymentMethods = useMemo(() => {
    if (!paymentSelected) {
      return {
        amazonPay: 'auto' as const,
        applePay: 'always' as const,
        googlePay: 'always' as const,
        link: 'auto' as const,
        paypal: 'auto' as const,
        klarna: 'auto' as const,
      };
    }
    return {
      amazonPay: paymentSelected === 'amazon_pay' ? ('auto' as const) : ('never' as const),
      applePay: paymentSelected === 'apple_pay' ? ('always' as const) : ('never' as const),
      googlePay: paymentSelected === 'google_pay' ? ('always' as const) : ('never' as const),
      link: paymentSelected === 'link' ? ('auto' as const) : ('never' as const),
      paypal: paymentSelected === 'paypal' ? ('auto' as const) : ('never' as const),
      klarna: paymentSelected === 'klarna' ? ('auto' as const) : ('never' as const),
    };
  }, [paymentSelected]);

  if (isEnableCreditLine(account) || !isStripeExpressEnabled) {
    return null;
  }

  const onConfirm = async (event: any) => {
    if (!stripe || !elements) return;
    await elements.submit();

    const values = UrlUtils.getValues();
    const quote = values[QUOTE_LABEL] ? (values[QUOTE_LABEL] as string) : undefined;
    const paymentDescription = paymentDescriptionFromExpressType(event?.expressPaymentType);

    if (type === StripeBtnTypes.Cars) {
      const carsData: any = {
        id: car?.id,
        customer: {
          primary: {
            personName: { givenName: driver?.givenName, surname: driver?.surname },
            telephone: { phoneTechType: '1', phoneNumber: driver?.phone },
            email: driver?.email,
            address: {
              streetNmbr: stripeInfo?.addressLine,
              cityName: stripeInfo?.city,
              postalCode: stripeInfo?.zipCode,
              stateCode: stripeInfo?.state ? stripeInfo?.state : 'AK',
              stateProv: stripeInfo?.state ? stripeInfo?.state : 'AK',
              countryCode: driver?.country,
            },
            countryCode: stripeInfo?.country,
            birthDate: `${moment(driver?.birthDate, DATE_FORMAT_FILTER).format('yyyy-MM-DD')}`,
            language: 'EN',
          },
        },
        currency: account?.currency,
        marginator: {
          percentage: marginatorStore.percentage,
          commission: marginatorStore.commission,
          impersonator: { memberId: marginatorStore.impersonator.memberId },
        },
        isCheckoutExpress: true,
      };
      applyExpressWalletSsoToPayload(
        carsData,
        { account, userWallet, selectedClientCash: selectedCarsReviewClientCash },
        { carsImpersonationLifeType: true },
      );
      // No `card` goes out on express, so the billing address the guest typed rides along as
      // `billingAddress` — `customer.primary.address` only reaches the booking record, not the
      // payment record. Initial request only: the express resume is a GET and cannot take it.
      const billingAddress = buildCarsBillingAddressPayload(stripeInfo, driver);
      if (billingAddress) {
        carsData.billingAddress = billingAddress;
      }
      dispatch(
        getCarsBookPackageWithCard(carsData, stripe as any, elements as any, paymentDescription),
      );
    } else if (type === StripeBtnTypes.Hotel) {
      const sessionKey = getSessionObj(
        values[SESSION_KEY_LABEL_ROOMS] as ISessionKey,
        values[SESSION_KEY_LABEL] as ISessionKey,
      );
      const getLeadPax = guests.find((guest: any) => guest?.isMainGuest);
      const data: any = {
        sessionKey,
        bookingPrice: bookingSummary?.bookingCard.bookingPrice.payNowPrice,
        hotelId: bookingSummary?.bookingCard.package.hotelId,
        leadPaxId: getLeadPax?.id,
        leadPaxRoomId: getLeadPax?.allocation,
        packageId: bookingSummary?.bookingCard.package.packageId,
        marginator: {
          percentage: marginatorStore.percentage,
          commission: marginatorStore.commission,
          impersonator: { memberId: marginatorStore.impersonator.memberId },
        },
        isCheckoutExpress: true,
        guests: guests.map((guest: any) => ({
          ...guest,
          namePrefix: !isEmpty(guest?.namePrefix) ? guest?.namePrefix : 'Mr',
          givenName: !isEmpty(guest?.givenName) ? guest?.givenName : 'TBV',
          surname: !isEmpty(guest?.surname) ? guest?.surname : 'TBV',
          type: guest?.type,
        })),
        currency: account?.currency,
        referenceNumber,
        agencyNumber,
        couponCode: coupon,
        quote,
      };
      applyExpressWalletSsoToPayload(data, {
        account,
        userWallet,
        selectedClientCash: selectedHotelReviewClientCash,
      });
      // No `card` goes out on express — the address collected by the address-only form that
      // replaced it does, as `billingAddress`, so the traveler record still carries it.
      const billingAddress = buildBillingAddressPayload({
        ...card,
        holderName: [getLeadPax?.givenName, getLeadPax?.surname].filter(Boolean).join(' '),
        phone: card?.phone || getLeadPax?.phone,
      });
      if (billingAddress) {
        data.billingAddress = billingAddress;
      }
      const customerLoyalty = localStorage.getItem(GET_CUSTOMER_LOYALTY) ?? '';
      if (!isEmpty(customerLoyalty)) {
        data.customerLoyalty = customerLoyalty;
      }
      dispatch(
        getBookPackageWithCard(data, stripe as any, elements as any, paymentDescription),
      );
    } else if (type === StripeBtnTypes.Condo) {
      const sessionKey = getSessionObj(
        values[CONDO_UNITS_SESSION_KEY_LABEL] as ISessionKey,
        values[CONDO_SESSION_KEY] as ISessionKey,
      );
      const data: any = {
        sessionKey,
        bookingPrice: condoBookingSummary?.bookingCard?.bookingPrice?.payNowPrice ?? 0,
        condoId: getCondoId(),
        availabilityId: getAvailabilityId(),
        couponCode: coupon,
        guests: condoGuest
          ? [{ ...condoGuest, phone: removingDashFromString(condoGuest.phone || '') }]
          : [],
        supplierType: condoBookingSummary?.supplierType ?? '',
        quote,
        isCheckoutExpress: true,
      };
      applyExpressWalletSsoToPayload(data, {
        account,
        userWallet,
        selectedClientCash: selectedCondoReviewClientCash,
      });
      // No `card` goes out on express, so the primary address rides along as `billingAddress` —
      // inside `guests[]` it only reaches the booking record, not the payment record. Initial
      // request only: the express resume is a GET and cannot take it.
      const billingAddress = buildGuestBillingAddressPayload(
        condoGuest,
        getCountryIsoCode(condoGuest?.country, condoBookingSummary?.supplierType, condosCountriesCode),
      );
      if (billingAddress) {
        data.billingAddress = billingAddress;
      }
      dispatch(
        getCondoBookPackageWithCard(
          data,
          () => scrollToError?.(),
          stripe,
          elements as any,
          paymentDescription,
        ),
      );
    } else if (type === StripeBtnTypes.Weeks) {
      const sessionKey = getSessionObj(
        values[WEEKS_UNITS_SESSION_KEY_LABEL] as ISessionKey,
        values[CONDO_SESSION_KEY] as ISessionKey,
      );
      const data: any = {
        sessionKey,
        bookingPrice: weeksBookingSummary?.bookingCard?.bookingPrice?.payNowPrice ?? 0,
        condoId: getCondoId() ? getCondoId() : null,
        availabilityId: getWeeksAvailabilityId() ? getWeeksAvailabilityId() : null,
        couponCode: coupon,
        guests: weeksGuest
          ? [{ ...weeksGuest, phone: removingDashFromString(weeksGuest.phone || '') }]
          : [],
        supplierType: weeksBookingSummary?.supplierType ?? '',
        quote,
        isCheckoutExpress: true,
      };
      applyExpressWalletSsoToPayload(data, {
        account,
        userWallet,
        selectedClientCash: selectedWeeksReviewClientCash,
      });
      // No `card` goes out on express, so the primary address rides along as `billingAddress` —
      // inside `guests[]` it only reaches the booking record, not the payment record. Initial
      // request only: the express resume is a GET and cannot take it.
      const billingAddress = buildGuestBillingAddressPayload(
        weeksGuest,
        getCountryIsoCode(weeksGuest?.country, weeksBookingSummary?.supplierType, condosCountriesCode),
      );
      if (billingAddress) {
        data.billingAddress = billingAddress;
      }
      dispatch(
        getWeeksBookPackageWithCard(
          data,
          () => scrollToError?.(),
          stripe,
          elements as any,
          paymentDescription,
        ),
      );
    }
  };

  const onReady = (event: any) => {
    const available = event?.availablePaymentMethods;
    if (available === undefined || available === null) return;
    // Always flip the probe flag off on any onReady so the spinner is replaced once we have data.
    onProbeComplete();
    // Only refresh `multiplePayments` while we're in the initial all-wallets probe. After the user
    // picks a wallet the EC is re-keyed with a single-method `paymentMethods` config, and Stripe's
    // next onReady reports only that one method as available — using it would wipe the other rows
    // from the picker and trap the user on their first pick.
    if (paymentSelected) return;
    if (hasStripeExpressWalletMethods(available)) {
      onMultiplePaymentsResolved(available);
    } else {
      onMultiplePaymentsResolved({});
    }
  };

  /** EC is hidden until the user picks a wallet — Back must still render so the user can exit. */
  const showEC = !!paymentSelected;

  return (
    <div className="force-full-stripe-checkout__action force-full-stripe-checkout__action--wallet">
      {forceFullStripeBack ? (
        <WhiteButton
          htmlType="button"
          className="force-full-stripe-checkout__back"
          onClick={forceFullStripeBack.onClick}
          disabled={forceFullStripeBack.disabled}
        >
          <FormattedMessage id="review.book.checkout.back" />
        </WhiteButton>
      ) : null}
      <div
        className="force-full-stripe-checkout__wallet-element"
        style={showEC ? undefined : { display: 'none' }}
      >
        <ExpressCheckoutElement
          key={JSON.stringify(paymentMethods)}
          onConfirm={onConfirm}
          onReady={onReady}
          options={{
            paymentMethods,
            paymentMethodOrder: ['applePay', 'googlePay', 'link', 'paypal', 'amazonPay', 'klarna'],
          }}
        />
      </div>
    </div>
  );
}
